import React, { useEffect, useMemo, useRef } from "react";

import { InfoMessage, Picto, Spinner, Title } from "@amphore-dev/amphore-lib";
import { AxiosError } from "axios";
import { format } from "date-fns";
import { t } from "i18next";
import { useBlocker } from "react-router-dom";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import TasksService from "@/services/TasksService";

import { useActiveProject } from "@/hooks/useActiveProject";
import { useCanWriteTasks } from "@/hooks/useActiveRole";
import { useHomeLocation } from "@/hooks/useHomeLocation";
import { useHomeNavigation } from "@/hooks/useHomeNavigation";
import { usePanelState } from "@/hooks/usePanelState";
import { useTasksFilters } from "@/hooks/useTasksFilters";

import { Tag, TaskDateRange } from "@/components/atoms";
import { EditPanel } from "@/components/organisms/EditPanel/EditPanel";
import { TaskView } from "@/components/organisms/TaskView/TaskView";

import { getApiErrormessage } from "@/utils/UAPIErrors";
import { getMonthRange } from "@/utils/UDates";
import { periodOfTask } from "@/utils/UTasks";

import { isTaskDependentQuery, taskKeys } from "@/constants/CQueryKeys";

import { TTask, TApiError, TTaskEditFormValues } from "@/types";

import { TaskEditForm } from "@/forms/index";
import { promiseToast } from "@/modules/index";

interface ITaskPanelProps {
	/** None: the new-task form. */
	taskId?: number;
}

/**
 * Whether the displayed period shows any day of the task's own. No
 * period set = the current month, which is what the list shows then.
 */
const showsTask = (
	period: { dateFrom?: string; dateTo?: string },
	task: Pick<TTask, "startDate" | "endDate">
): boolean => {
	const shown =
		period.dateFrom && period.dateTo
			? (period as { dateFrom: string; dateTo: string })
			: (getMonthRange() as { dateFrom: string; dateTo: string });
	const own = periodOfTask(task);
	return own.dateFrom! <= shown.dateTo && own.dateTo! >= shown.dateFrom;
};

/**
 * The task side panel, as the URL describes it: /tasks/:id shows the
 * task, ?edit=1 edits it, /tasks/new is the form for a new one - started
 * from ?project, ?date and ?from (a task to copy). Opening a task the
 * displayed period does not show moves the period to the task's days.
 * Leaving with unsaved changes - cancel, close, but also the back button
 * or any link - asks first.
 */
export const TaskPanel: React.FC<ITaskPanelProps> = ({ taskId }) => {
	const queryClient = useQueryClient();
	const { isEditing: editInUrl, prefill } = useHomeLocation();
	const { toTask, closeTask } = useHomeNavigation();
	const { project: activeProject } = useActiveProject();
	// Same rule as TasksList: a VIEWER of the filtered project reads, and
	// the Edit button is not offered.
	const canWrite = useCanWriteTasks();
	const { filters, setFilters } = useTasksFilters();
	const { formRef, showConfirmModal, makeActions } =
		usePanelState<TTaskEditFormValues>();

	const { isLoading, data, error } = useQuery<TTask, AxiosError<TApiError>>({
		queryKey: taskKeys.detail(taskId ?? "new"),
		queryFn: () => TasksService.fetchTask(taskId!),
		enabled: !!taskId,
	});

	// A duplicate: the new form starts from the source task (name, notes,
	// project...), fetched by id - the URL only carries `?from=`.
	const { fromTaskId } = prefill;
	const { data: source, isLoading: isLoadingSource } = useQuery<TTask>({
		queryKey: taskKeys.detail(fromTaskId),
		queryFn: () => TasksService.fetchTask(fromTaskId!),
		enabled: !taskId && !!fromTaskId,
	});
	const copied = useMemo<Partial<TTask>>(
		() =>
			source
				? {
						name: source.name,
						notes: source.notes,
						workedOn: source.workedOn,
						project: source.project,
					}
				: {},
		[source]
	);

	const isNew = !taskId;
	const isEditingActive = isNew || editInUrl;

	// The period shows the task, or moves to its days.
	useEffect(() => {
		if (data && !showsTask(filters, data)) setFilters(periodOfTask(data));
		// Only when the task changes: a period the user moves afterwards
		// is theirs.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data]);

	// Every navigation while the form is dirty is blocked and confirmed,
	// except the ones this panel makes after its own confirm (`leaving`).
	const leaving = useRef(false);
	const blocker = useBlocker(
		() => isEditingActive && !leaving.current && !!formRef.current?.dirty
	);
	const leave = (go: () => void) => {
		leaving.current = true;
		go();
	};
	useEffect(() => {
		leaving.current = false;
	});

	// A save still on its way completes even if the user moved on - the
	// request is out, and reset() only detaches the observer, onSuccess
	// runs anyway. It must not then reopen the task they closed (the panel
	// is gone: ×, Escape, the backdrop) or pull them back from the one they
	// opened since (same panel, another task).
	const mounted = useRef(true);
	useEffect(() => {
		mounted.current = true;
		return () => {
			mounted.current = false;
		};
	}, []);
	const shownTaskId = useRef(taskId);
	shownTaskId.current = taskId;

	const submitMutation = useMutation({
		onMutate: () => ({ savedFrom: taskId }),
		mutationFn: (values: TTaskEditFormValues) => {
			const payload = {
				...values,
				projectId: values.project?.id,
				startDate:
					format(values.startDate, "yyyy-MM-dd") +
					" " +
					values.startTime +
					":00",
				endDate: values.endDate
					? format(values.endDate, "yyyy-MM-dd") +
						" " +
						values.endTime +
						":00"
					: "",
			};
			const prom = taskId
				? TasksService.updateTask(payload)
				: TasksService.createTask(payload);
			promiseToast(prom);
			return prom;
		},
		onSuccess: (task, _values, context) => {
			if (task.id)
				queryClient.setQueryData(taskKeys.detail(task.id), task);
			queryClient.invalidateQueries({
				// The figures, summaries and report listings count it too.
				predicate: (query) => isTaskDependentQuery(query.queryKey),
			});
			if (!mounted.current || shownTaskId.current !== context?.savedFrom)
				return;
			// Saved: the task, in view mode (a new one replaces the form's
			// address, so "back" does not reopen an empty form).
			leave(() => toTask(task.id, { replace: true }));
		},
	});

	useEffect(() => {
		const project = data?.project || copied.project;
		if (project) formRef.current?.setFieldValue("project", project);
	}, [copied.project, data?.project, formRef]);

	// Cancel: an existing task goes back to view mode, a new one closes.
	const handleCancel = () =>
		leave(() => (taskId ? toTask(taskId, { replace: true }) : closeTask()));
	// The cross: closes the panel.
	const handleClose = () => {
		if (submitMutation.isPending) submitMutation.reset();
		leave(() => closeTask());
	};

	const { onSave, onCancel, onDiscard, onKeepEditing } =
		makeActions(handleCancel);

	const blocked = blocker.state === "blocked";
	const initialValues = { ...copied, ...data };
	const prefillDate = prefill.date
		? new Date(`${prefill.date}T00:00:00`)
		: new Date();

	return (
		<EditPanel
			isEditing={isEditingActive}
			isLoading={submitMutation.isPending}
			editTitle={isNew ? t("tasks.newTask") : t("actions.editing")}
			viewHeader={
				data ? (
					<TaskViewHeader
						task={data}
						isLoading={isLoading || submitMutation.isPending}
						// Unfiltered, the list spans projects: the task's own
						// project decides whether it is archived.
						disabled={
							!!error || !canWrite || !!data.project?.isArchived
						}
						onEdit={() =>
							toTask(data.id, { edit: true, replace: true })
						}
					/>
				) : undefined
			}
			onClose={handleClose}
			// Escape / the backdrop: a plain navigation, so a dirty form
			// still gets the router's confirm (unlike the × above).
			onDismiss={closeTask}
			onCancel={onCancel}
			onSave={onSave}
			showConfirmModal={showConfirmModal || blocked}
			onDiscard={blocked ? () => blocker.proceed() : onDiscard}
			onKeepEditing={blocked ? () => blocker.reset() : onKeepEditing}
		>
			{error ? (
				<InfoMessage color="danger">
					{getApiErrormessage(error)}
				</InfoMessage>
			) : isLoading || isLoadingSource ? (
				// The form takes its initial values once: the source must be
				// there before it mounts.
				<Spinner />
			) : isEditingActive ? (
				<TaskEditForm
					onSubmit={(values) => submitMutation.mutateAsync(values)}
					initialValues={{
						...initialValues,
						project:
							data?.project ?? copied.project ?? activeProject,
						startTime: format(
							data?.startDate ?? new Date(),
							"HH:mm"
						),
						endTime: data?.endDate
							? format(data.endDate, "HH:mm")
							: "",
						startDate: data?.startDate
							? new Date(data.startDate)
							: prefillDate,
						endDate: data?.endDate ? new Date(data.endDate) : null,
					}}
					innerRef={formRef}
				/>
			) : (
				<TaskView task={data} />
			)}
		</EditPanel>
	);
};

interface TaskViewHeaderProps {
	task: TTask;
	isLoading: boolean;
	disabled: boolean;
	onEdit: () => void;
}

const TaskViewHeader: React.FC<TaskViewHeaderProps> = ({
	task,
	isLoading,
	disabled,
	onEdit,
}) => (
	<div className="space-y-1">
		<Title size="h3">{task.name}</Title>
		<div className="mt-2! flex items-center gap-2 text-neutral-500">
			<Tag className="py-0" color={task.project?.color}>
				{task.project?.name}
			</Tag>
			<div className="row gap-1">
				<TaskDateRange start={task.startDate} end={task.endDate} />
			</div>
		</div>
		{!isLoading && !disabled && (
			<button
				onClick={onEdit}
				className="flex items-center gap-2 text-neutral-500 hover:text-black"
			>
				{t("actions.edit")}
				<Picto icon="edit" className="h-4 w-4" />
			</button>
		)}
	</div>
);
